import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { useAuthContext } from '@/contexts/AuthContext';
import { useSidebar } from '@/contexts/SidebarContext';
import { supabase } from '@/lib/supabase';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  User,
  LogOut,
  Settings,
  Bell,
  Clock,
  Package,
  Link2,
  FileText,
  Shield,
  Users,
  CreditCard,
  Building,
  Calendar as CalendarIcon,
  Folder,
  GraduationCap,
  BookOpen,
  Layers,
  HeartHandshake,
  ClipboardList,
  ListChecks,
  Sparkles,
  Receipt,
  BarChart,
  ShoppingCart,
  Mail,
  Plus,
  ExternalLink,
  UserCog,
  Cog,
  ExternalLink as LinkExternal,
  FolderClock,
  NotebookPen,
  FileCheck2,
  Briefcase,
  FileSignature,
  FileTerminal,
  TicketIcon,
  Inbox,
  Headphones,
  Warehouse,
  Building2,
  LayoutDashboard,
  Home,
  CheckSquare,
  HelpCircle,
  Menu,
  School,
  Download,
  Beaker,
  MessageSquare,
  MoreVertical,
  Circle
} from 'lucide-react';
import { Button } from '@/components/ui/button';

interface MenuItem {
  title: string;
  icon?: React.ComponentType<any>;
  href?: string;
  submenu?: MenuItem[];
  badge?: string;
  description?: string;
  isNew?: boolean;
}

const sidebarItems: MenuItem[] = [
  {
    title: 'Página Inicial',
    href: '/',
    icon: Home,
    description: 'Visão geral do sistema'
  },
  {
    title: 'Assinaturas',
    icon: CreditCard,
    description: 'Gestão de contratações',
    submenu: [
      {
        title: 'Assinaturas',
        href: '/subscriptions',
        icon: CreditCard,
        description: 'Todas as assinaturas ativas'
      },
      {
        title: 'Catálogo de Produtos',
        href: '/product-catalog',
        icon: Package,
        description: 'Produtos e planos disponíveis'
      },
      {
        title: 'Clientes',
        href: '/customers',
        icon: Building,
        description: 'Base de clientes'
      },
      {
        title: 'Faturas',
        href: '/invoices',
        icon: Receipt,
        description: 'Faturamento e cobrança'
      },
    ],
  },
  {
    title: 'Formações',
    icon: GraduationCap,
    description: 'Gestão educacional',
    submenu: [
      {
        title: 'Formações',
        href: '/classes',
        icon: Folder,
        description: 'Cursos e turmas'
      },
      {
        title: 'Calendário',
        href: '/sessions-calendar',
        icon: CalendarIcon,
        description: 'Visualização profissional do calendário',
        isNew: true
      },
      {
        title: 'Cursos',
        href: '/courses',
        icon: BookOpen,
        description: 'Catálogo de cursos'
      },
    ],
  },
  {
    title: 'Vendas',
    icon: Briefcase,
    description: 'Pipeline comercial',
    submenu: [
      {
        title: 'Oportunidades',
        href: '/business-opportunities',
        icon: Briefcase,
        description: 'Leads e negociações',
        badge: '12'
      },
    ],
  },
  {
    title: 'E-Commerce',
    icon: ShoppingCart,
    description: 'Loja virtual',
    isNew: true,
    submenu: [
      {
        title: 'Transações',
        href: '/transactions',
        icon: CreditCard,
        description: 'Histórico de vendas'
      },
      {
        title: 'Vouchers',
        href: '/voucher-codes',
        icon: FileText,
        description: 'Códigos promocionais'
      },
    ],
  },
  {
    title: 'Analytics',
    icon: BarChart,
    description: 'Análise de dados e métricas',
    submenu: [
      {
        title: 'MAU por Cliente',
        href: '/analytics/mau',
        icon: BarChart,
        description: 'Usuários ativos mensais'
      },
    ],
  },
];

const bottomItems: MenuItem[] = [
  {
    title: 'Configurações',
    href: '/settings',
    icon: Settings,
    description: 'Preferências do sistema'
  },
];

const Sidebar = () => {
  const { collapsed, setCollapsed, isMobile, setLinkClicked } = useSidebar();
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const location = useLocation();
  const { user } = useAuthContext();
  const { toast } = useToast();

  const displayUser = useMemo(() => user || {
    id: 'temp-user-id',
    email: 'usuario@exemplo.com',
    user_metadata: {
      full_name: 'Usuário Temporário',
      avatar_url: '',
    }
  }, [user]);

  const [userProfile, setUserProfile] = useState<any>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  const fetchAvatarImage = useCallback(async (filePath: string) => {
    try {
      const { data, error } = await supabase
        .storage
        .from('avatars')
        .download(`users/${filePath.split('/').pop()}`);
        
      if (error) {
        console.error('Sidebar - Erro ao baixar avatar:', error);
        return;
      }
      
      const url = URL.createObjectURL(data);
      setAvatarUrl(url);
    } catch (error) {
      console.error('Sidebar - Erro ao processar avatar:', error);
    }
  }, []); // Empty dependency array to prevent recreation

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      
      // Limpar dados antigos ao trocar de usuário
      setUserProfile(null);
      setAvatarUrl(null);

      try {
        const { data, error } = await supabase
          .from('users')
          .select('profile_image_file_url, first_name, last_name')
          .eq('id', user.id)
          .single();
        
        if (error) {
          // Não logar erro se for "No rows found", é um caso esperado para novos usuários
          if (error.code !== 'PGRST116') {
            console.warn('Sidebar - Erro ao buscar perfil:', error);
          }
          return;
        }

        if (data) {
          setUserProfile(data);
          if (data.profile_image_file_url) {
            // Call fetchAvatarImage directly to avoid dependency issues
            fetchAvatarImage(data.profile_image_file_url);
          }
        }
      } catch (error) {
        console.error('Sidebar - Erro crítico ao buscar perfil do usuário:', error);
      }
    };
    
    fetchUserProfile();
  }, [user]); // Remove fetchAvatarImage dependency to prevent infinite loop

  const toggleSidebar = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed, setCollapsed]);

  const toggleSubmenu = useCallback((title: string) => {
    setExpandedMenus(prev => 
      prev.includes(title) 
        ? prev.filter(item => item !== title)
        : [...prev, title]
    );
  }, []);

  const handleLinkClick = useCallback(() => {
    setLinkClicked(true);
    if (!isMobile && !collapsed) {
      setCollapsed(true);
    }
  }, [isMobile, collapsed, setCollapsed, setLinkClicked]);

  const renderMenuItem = useCallback((item: MenuItem) => {
    const isActive = item.href 
      ? location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href))
      : false;
    const isExpanded = expandedMenus.includes(item.title);
    const hasActiveChild = item.submenu?.some(subitem => 
      subitem.href && (location.pathname === subitem.href || (subitem.href !== '/' && location.pathname.startsWith(subitem.href)))
    );

    const renderIcon = (Icon: React.ComponentType<any> | undefined, active: boolean = false) => {
      if (!Icon) return null;
      return <Icon className={cn("h-5 w-5 transition-colors", active ? "text-blue-600" : "text-gray-500 group-hover:text-gray-700")} />;
    };

    const MenuItemContent = ({ children }: { children: React.ReactNode }) => (
      <TooltipProvider>
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>{children}</TooltipTrigger>
                     {collapsed && (
             <TooltipContent side="right" className="ml-2">
               <div className="text-sm font-bold">{item.title}</div>
               {item.description && <div className="text-xs text-gray-500 mt-1">{item.description}</div>}
             </TooltipContent>
           )}
        </Tooltip>
      </TooltipProvider>
    );

    if (item.submenu) {
      return (
        <div key={item.title}>
          <MenuItemContent>
            <button
              onClick={() => {
                if (collapsed) {
                  setCollapsed(false);
                  if (!isExpanded) toggleSubmenu(item.title);
                } else {
                  toggleSubmenu(item.title);
                }
              }}
              className={cn(
                'group w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-bold transition-all',
                'hover:bg-gray-50',
                (hasActiveChild || isExpanded) && 'bg-blue-50 text-blue-700',
                collapsed && 'justify-center'
              )}
            >
              <div className="flex items-center gap-3">
                {renderIcon(item.icon, hasActiveChild || isExpanded)}
                {!collapsed && (
                  <div>
                    <div className="flex items-center gap-2">
                      <span>{item.title}</span>
                      {item.isNew && <Badge variant="secondary" className="bg-green-100 text-green-700">Novo</Badge>}
                    </div>
                    {item.description && <div className="text-xs text-gray-500 mt-0.5 text-left truncate">{item.description}</div>}
                  </div>
                )}
              </div>
              {!collapsed && <ChevronDown className={cn('h-4 w-4 transition-transform', isExpanded && 'rotate-180')} />}
            </button>
          </MenuItemContent>
          {!collapsed && isExpanded && (
            <div className="ml-5 pl-2.5 border-l space-y-1 mt-1">
              {item.submenu.map(subitem => {
                const isSubActive = subitem.href && (location.pathname === subitem.href || (subitem.href !== '/' && location.pathname.startsWith(subitem.href)));
                return (
                  <Link
                    key={subitem.href}
                    to={subitem.href!}
                    onClick={handleLinkClick}
                    className={cn(
                      'group flex items-center gap-3 px-2.5 py-1.5 rounded-md text-sm',
                      'hover:bg-gray-50',
                      isSubActive ? 'bg-blue-100 text-blue-700 font-bold' : 'text-gray-600'
                    )}
                  >
                    <Circle className={cn("h-2 w-2", isSubActive ? "fill-blue-600 text-blue-600" : "text-gray-300")} />
                    <span>{subitem.title}</span>
                    {subitem.badge && <Badge variant="secondary" className="ml-auto bg-orange-100 text-orange-700">{subitem.badge}</Badge>}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <MenuItemContent key={item.href}>
        <Link
          to={item.href!}
          onClick={() => {
            if (collapsed) setCollapsed(false);
            handleLinkClick();
          }}
          className={cn(
            'group flex items-center gap-3 px-2.5 py-2 rounded-lg text-sm font-bold transition-all',
            'hover:bg-gray-50',
            isActive ? 'bg-blue-100 text-blue-700' : 'text-gray-700',
            collapsed && 'justify-center'
          )}
        >
          {renderIcon(item.icon, isActive)}
          {!collapsed && (
            <div>
              <div className="flex items-center gap-2">
                <span>{item.title}</span>
                {item.isNew && <Badge variant="secondary" className="bg-green-100 text-green-700">Novo</Badge>}
              </div>
              {item.description && <div className="text-xs text-gray-500 mt-0.5 text-left truncate">{item.description}</div>}
            </div>
          )}
        </Link>
      </MenuItemContent>
    );
  }, [location.pathname, expandedMenus, collapsed, isMobile, setCollapsed, toggleSubmenu, handleLinkClick]);

  return (
    <div className={cn('fixed inset-y-0 left-0 z-50 flex flex-col bg-white border-r transition-all duration-300', collapsed ? 'w-16' : 'w-64')}>
      <div className={cn(
        'flex items-center h-16 border-b shrink-0',
        collapsed ? 'justify-center px-3' : 'justify-between px-4'
      )}>
        <Link to="/" className="flex items-center gap-3 group">
          {collapsed ? (
            <div className="p-2 rounded-lg bg-blue-50 group-hover:bg-blue-100 transition-colors">
              <img 
                src="https://asset.brandfetch.io/idNdb_MPJ2/idNIwj9zYC.png?c=1dxbfHSJFAPEGdCLU4o5B" 
                alt="Trivium Logo" 
                className="h-6 w-6 object-contain"
              />
            </div>
          ) : (
            <>
              <div className="p-2 rounded-lg bg-blue-50">
                <img 
                  src="https://asset.brandfetch.io/idNdb_MPJ2/idNIwj9zYC.png?c=1dxbfHSJFAPEGdCLU4o5B" 
                  alt="Trivium Logo" 
                  className="h-6 w-6 object-contain"
                />
              </div>
              <div>
                <div className="text-lg font-bold">Trivium</div>
                <div className="text-xs text-gray-500">Microsoft Partner</div>
              </div>
            </>
          )}
        </Link>
        {!collapsed && (
          <Button variant="ghost" size="icon" onClick={toggleSidebar} className="h-8 w-8">
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="flex-1 overflow-auto p-2">
        <nav className="space-y-1">
          {sidebarItems.map(renderMenuItem)}
        </nav>
      </div>

      <div className="p-2">
        <nav className="space-y-1">
          {bottomItems.map(renderMenuItem)}
        </nav>
      </div>

      <div className="p-3 border-t">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/profile" className={cn('group flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100', collapsed && 'justify-center')}>
                <Avatar className="h-9 w-9">
                  <AvatarImage src={avatarUrl || ""} alt="User" />
                  <AvatarFallback className="bg-blue-500 text-white font-bold">
                    {userProfile?.first_name && userProfile?.last_name
                      ? `${userProfile.first_name[0]}${userProfile.last_name[0]}`
                      : (displayUser?.user_metadata?.full_name?.substring(0, 2).toUpperCase() || 'U')}
                  </AvatarFallback>
                </Avatar>
                {!collapsed && (
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold truncate">
                      {userProfile?.first_name && userProfile?.last_name
                        ? `${userProfile.first_name} ${userProfile.last_name}`
                        : displayUser?.user_metadata?.full_name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{displayUser.email}</p>
                  </div>
                )}
              </Link>
            </TooltipTrigger>
                         {collapsed && (
               <TooltipContent side="right">
                 <p className="font-bold">
                   {userProfile?.first_name && userProfile?.last_name
                     ? `${userProfile.first_name} ${userProfile.last_name}`
                     : displayUser?.user_metadata?.full_name}
                 </p>
                 <p className="text-sm text-gray-500">{displayUser.email}</p>
               </TooltipContent>
             )}
          </Tooltip>
        </TooltipProvider>
      </div>


    </div>
  );
};

export default Sidebar;
